/**
 * Build 6B — editing and deleting what the executive desks keep as entered
 * notes: the ACA checklist (green program), the benchmark's peers and their
 * values, the improvement initiatives, and the expansion options' figures.
 *
 * Each desk keeps its whole entry as one site note (`executive.<desk>`, via
 * useEntered): the latest note is the current value, and every save is a new
 * note with who and when, so an edit or a delete keeps what it replaced. The
 * storage is not changed here; these are the pure steps from one value to the
 * next, and the checks a person is told about in words.
 */
import type { ChecklistEvidence } from "./green";
import type { BenchmarkEntered, Peer, PeerSet } from "./benchmark";
import type { ExpansionEntered } from "./expansion";
import type { ImprovementEntered, Initiative } from "./improvement";

/* ── ACA checklist ───────────────────────────────────────────────────── */

export function checklistRefusal(met: boolean, evidence: string): string | null {
  if (met && !evidence.trim()) return "Say what shows it is met: the verifier and date, or where the document is held.";
  if (evidence.length > 500) return "Keep the evidence under 500 characters.";
  return null;
}

export function setChecklistItem(
  checklist: Record<string, ChecklistEvidence> | undefined,
  key: string,
  item: ChecklistEvidence | null,
): Record<string, ChecklistEvidence> {
  const next = { ...(checklist ?? {}) };
  if (item) next[key] = item;
  else delete next[key];
  return next;
}

/* ── benchmark peers ─────────────────────────────────────────────────── */

export const PEER_SETS: PeerSet[] = ["local", "regional", "global"];

export function peerRefusal(
  peer: { code: string; name: string; set: string },
  list: readonly Peer[],
  siteCode: string,
): string | null {
  const code = peer.code.trim().toUpperCase();
  if (!/^[A-Z]{4}$/.test(code)) return "A peer is named by its four-letter ICAO code, such as OMDB.";
  if (code === siteCode.toUpperCase()) return "That is this airport, not a peer.";
  if (list.some((p) => p.code === code)) return "That airport is already a peer.";
  if (!peer.name.trim()) return "Give the peer airport's name.";
  if (!(PEER_SETS as string[]).includes(peer.set)) return "Choose local, regional or global.";
  return null;
}

export function addPeer(entered: BenchmarkEntered, list: readonly Peer[], peer: Peer): BenchmarkEntered {
  return { ...entered, peerList: [...list, { ...peer, code: peer.code.trim().toUpperCase(), name: peer.name.trim() }] };
}

/** Removes the peer and every value entered for it — a peer off the list must not rank anything. */
export function removePeer(entered: BenchmarkEntered, list: readonly Peer[], code: string): BenchmarkEntered {
  const peers = { ...(entered.peers ?? {}) };
  delete peers[code];
  return { ...entered, peerList: list.filter((p) => p.code !== code), peers };
}

export function peerValueRefusal(v: { value: number; year?: number; source: string }, now = new Date()): string | null {
  if (!Number.isFinite(v.value)) return "The value must be a number.";
  if (v.year != null && (!Number.isInteger(v.year) || v.year < 1990 || v.year > now.getFullYear()))
    return "The year must be between 1990 and this year.";
  if (!v.source.trim()) return "Say where the figure was published. A peer value is never estimated.";
  return null;
}

/** `who` is a peer code, or "own" for the airport's own entered figure. */
export function removePeerValue(entered: BenchmarkEntered, who: string, metric: string): BenchmarkEntered {
  if (who === "own") {
    const own = { ...(entered.own ?? {}) };
    delete own[metric];
    return { ...entered, own };
  }
  const forPeer = { ...(entered.peers?.[who] ?? {}) };
  delete forPeer[metric];
  const peers = { ...(entered.peers ?? {}), [who]: forPeer };
  if (!Object.keys(forPeer).length) delete peers[who];
  return { ...entered, peers };
}

/* ── improvement initiatives ─────────────────────────────────────────── */

export type EnteredInitiative = NonNullable<ImprovementEntered["initiatives"]>[number];

export function initiativeRefusal(i: { name: string; valuePerYear?: number; cost?: number }): string | null {
  if (!i.name.trim()) return "Give the initiative a name.";
  if (i.valuePerYear != null && (!Number.isFinite(i.valuePerYear) || i.valuePerYear < 0))
    return "The value a year cannot be negative.";
  if (i.cost != null && (!Number.isFinite(i.cost) || i.cost < 0)) return "The cost cannot be negative.";
  return null;
}

export function updateInitiative(
  entered: ImprovementEntered,
  key: string,
  patch: Partial<Omit<EnteredInitiative, "key">>,
): ImprovementEntered {
  return {
    ...entered,
    initiatives: (entered.initiatives ?? []).map((i) => (i.key === key ? { ...i, ...patch } : i)),
  };
}

export function removeInitiative(entered: ImprovementEntered, key: string): ImprovementEntered {
  return { ...entered, initiatives: (entered.initiatives ?? []).filter((i) => i.key !== key) };
}

export const INITIATIVE_STATUSES: Initiative["status"][] = ["planned", "running", "done"];

/* ── expansion options ───────────────────────────────────────────────── */

export interface OptionFigures {
  capacityPax?: number;
  stands?: number;
  cost?: number;
  months?: number;
  enabled?: boolean;
}

export function optionRefusal(o: OptionFigures): string | null {
  if (o.cost != null && (!Number.isFinite(o.cost) || o.cost < 0)) return "The cost cannot be negative.";
  if (o.months != null && (!Number.isInteger(o.months) || o.months < 1 || o.months > 240))
    return "The build time is a whole number of months from 1 to 240.";
  if (o.capacityPax != null && (!Number.isFinite(o.capacityPax) || o.capacityPax < 0))
    return "The added capacity cannot be negative.";
  if (o.stands != null && (!Number.isInteger(o.stands) || o.stands < 0))
    return "Stands are a whole number, zero or more.";
  return null;
}

/** `null` takes the option back to its starting figures. */
export function setOptionFigures(
  entered: ExpansionEntered,
  key: string,
  figures: OptionFigures | null,
): ExpansionEntered {
  const options = { ...(entered.options ?? {}) };
  if (figures) options[key] = figures;
  else delete options[key];
  return { ...entered, options };
}
